import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../api";
import toast from 'react-hot-toast';
import MainLayout from "./MainLayout";
import TopNavBar from "./TopNavBar";
import { useAuth } from "../context/AuthContext";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiCalendar,
  FiUser, FiUsers, FiBell, FiChevronDown, FiChevronUp, FiChevronLeft, FiChevronRight,
  FiTag, FiCheckSquare, FiFilter, FiRefreshCw,
  FiPaperclip, FiDownload, FiUploadCloud, FiLoader,
  FiList, FiGrid, FiLayers, FiFileText, FiSliders, FiEye
} from "react-icons/fi";
import "../styles/projectmaster.css";

/* ─── Status Config ─── */
const STATUS_OPTIONS = [
  { value: "New", label: "New", color: "#5c5ff6ff", bg: "#F5F3FF" },
  { value: "InProgress", label: "In Progress", color: "#F59E0B", bg: "#EFF6FF" },
  { value: "Onhold", label: "Onhold", color: "#f51b0bff", bg: "#FFFBEB" },
  { value: "Completed", label: "Completed", color: "#10B981", bg: "#ECFDF5" },
  { value: "Closed", label: "Closed", color: "#006400", bg: "#F3F4F6" },
];

const getDynamicStatuses = (projectStatuses) => {
  const finalStatuses = [];

  if (Array.isArray(projectStatuses) && projectStatuses.length > 0) {
    const sorted = [...projectStatuses].sort((a, b) => {
      const aOrder = a.display_order ?? a.sequence ?? a.id ?? 0;
      const bOrder = b.display_order ?? b.sequence ?? b.id ?? 0;
      return Number(aOrder) - Number(bOrder);
    });

    sorted.forEach(ps => {
      const val = ps.status_name || ps.statusName || ps.status_code || ps.id || "Unknown";
      const staticMatch = STATUS_OPTIONS.find(so =>
        so.value.toLowerCase().replace(/\s+/g, '') === val.toLowerCase().replace(/\s+/g, '') ||
        so.label.toLowerCase().replace(/\s+/g, '') === val.toLowerCase().replace(/\s+/g, '')
      );

      finalStatuses.push({
        value: val,
        label: val,
        color: ps.color_code || ps.color || (staticMatch ? staticMatch.color : "#4F6EF7"),
        bg: ps.bg_code || ps.bg || (staticMatch ? staticMatch.bg : "#eff6ff")
      });
    });
  }

  // Include any static statuses that aren't in the API, to preserve old data columns
  STATUS_OPTIONS.forEach(so => {
    const exists = finalStatuses.find(fs =>
      fs.value.toLowerCase().replace(/\s+/g, '') === so.value.toLowerCase().replace(/\s+/g, '') ||
      fs.label.toLowerCase().replace(/\s+/g, '') === so.label.toLowerCase().replace(/\s+/g, '')
    );
    if (!exists) {
      finalStatuses.push(so);
    }
  });

  return finalStatuses.length > 0 ? finalStatuses : STATUS_OPTIONS;
};

const getStatusConfig = (val, projectStatuses = []) => {
  const options = getDynamicStatuses(projectStatuses);
  const searchVal = String(val || "").toLowerCase().replace(/\s+/g, '');
  return options.find(s =>
    s.value.toLowerCase().replace(/\s+/g, '') === searchVal ||
    s.label.toLowerCase().replace(/\s+/g, '') === searchVal
  ) || options[0] || STATUS_OPTIONS[0];
};

/* ─── Format Date Time ─── */
const fmtDateTime = (d) => {
  if (!d) return "";
  try {
    let dateStr = d;
    if (typeof dateStr === 'string' && !dateStr.endsWith('Z') && !dateStr.includes('+')) {
      if (dateStr.includes('T')) dateStr += 'Z';
      else dateStr = dateStr.replace(' ', 'T') + 'Z';
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return d;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strTime = String(hours).padStart(2, '0') + ':' + minutes + ' ' + ampm;
    return `${day}-${month}-${year} ${strTime}`;
  } catch { return d; }
};

/* ─── Format Date Only ─── */
const fmtDateOnly = (d) => {
  if (!d) return "—";
  try {
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch { return d; }
};

/* ─── Helper to get Logged-in User Display Name ─── */
const getUserDisplayName = (user) => {
  if (user?.name) return user.name;
  if (user?.fullName) return user.fullName;
  if (user?.employee_name) return user.employee_name;
  if (user?.first_name) return `${user.first_name} ${user.last_name || ''}`.trim();
  if (user?.email) return user.email;
  if (user?.empId || user?.employeeId || user?.id) return user.empId || user.employeeId || user.id;
  try {
    const stored = localStorage.getItem("user") || localStorage.getItem("currentUser");
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.name || parsed.fullName || parsed.employee_name || parsed.email || "System";
    }
  } catch (e) { }
  return "System";
};

/* ─── Generate Project ID ─── */
const generateProjectId = () => {
  const prefix = "PRJ";
  const ts = Date.now().toString(36).toUpperCase();
  return `${prefix}-${ts}`;
};

/* ─── Rich Text Editor Toolbar ─── */
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header", "bold", "italic", "underline", "strike",
  "list", "bullet", "link",
];

/* ─── Multi-Select Dropdown ─── */
function MultiSelect({ options, selected, onChange, placeholder, label }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter(o =>
    `${o.label} ${o.id}`.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => {
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);
  };

  const selectedLabels = selected
    .map(id => options.find(o => o.id === id)?.label)
    .filter(Boolean);

  return (
    <div className="pm-multiselect" ref={ref} style={{ zIndex: open ? 10 : 1 }}>
      <div className="pm-multiselect-trigger" onClick={() => setOpen(o => !o)}>
        <div className="pm-multiselect-values">
          {selected.length === 0
            ? <span className="pm-placeholder">{placeholder}</span>
            : selected.length <= 2
              ? selected.map(id => {
                const o = options.find(opt => opt.id === id);
                if (!o) return null;
                return (
                  <span
                    key={id}
                    className="pm-tag"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    onMouseEnter={(e) => {
                      const icon = e.currentTarget.querySelector('.pm-tag-remove-icon');
                      if (icon) icon.style.display = 'inline-flex';
                    }}
                    onMouseLeave={(e) => {
                      const icon = e.currentTarget.querySelector('.pm-tag-remove-icon');
                      if (icon) icon.style.display = 'none';
                    }}
                  >
                    {o.label}
                    <span
                      className="pm-tag-remove-icon"
                      style={{ display: 'none', cursor: 'pointer', alignItems: 'center', color: '#64748b' }}
                      onClick={(e) => { e.stopPropagation(); toggle(id); }}
                    >
                      <FiX size={12} />
                    </span>
                  </span>
                );
              })
              : (() => {
                const firstId = selected[0];
                const firstO = options.find(opt => opt.id === firstId);
                return (
                  <>
                    <span
                      className="pm-tag"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      onMouseEnter={(e) => {
                        const icon = e.currentTarget.querySelector('.pm-tag-remove-icon');
                        if (icon) icon.style.display = 'inline-flex';
                      }}
                      onMouseLeave={(e) => {
                        const icon = e.currentTarget.querySelector('.pm-tag-remove-icon');
                        if (icon) icon.style.display = 'none';
                      }}
                    >
                      {firstO?.label}
                      <span
                        className="pm-tag-remove-icon"
                        style={{ display: 'none', cursor: 'pointer', alignItems: 'center', color: '#64748b' }}
                        onClick={(e) => { e.stopPropagation(); toggle(firstId); }}
                      >
                        <FiX size={12} />
                      </span>
                    </span>
                    <span className="pm-tag-more">+{selected.length - 1} more</span>
                  </>
                );
              })()
          }
        </div>
        {open ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
      </div>

      {open && (
        <div className="pm-multiselect-dropdown" style={{ zIndex: 999, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
          <div className="pm-multiselect-search" style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiSearch size={13} style={{ color: '#94a3b8' }} />
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search employees..."
              style={{ border: 'none', outline: 'none', width: '100%', fontSize: '13px' }}
            />
          </div>
          <div className="pm-multiselect-list" style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px 0' }}>
            {filtered.length === 0
              ? <div className="pm-multiselect-empty">No results</div>
              : filtered.map(o => (
                <label key={o.id} className={`pm-multiselect-item ${selected.includes(o.id) ? "checked" : ""}`}>
                  <input
                    type="checkbox"
                    checked={selected.includes(o.id)}
                    onChange={() => toggle(o.id)}
                  />
                  <div className="pm-emp-info">
                    <span className="pm-emp-name">{o.label}</span>
                  </div>
                  {selected.includes(o.id) && <FiCheckSquare size={14} className="pm-check-icon" />}
                </label>
              ))
            }
          </div>
          {selected.length > 0 && (
            <div className="pm-multiselect-footer">
              <button onClick={() => onChange([])}>Clear all</button>
              <span>{selected.length} selected</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Single Select (Employee) ─── */
function SingleSelect({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter(o =>
    `${o.label} ${o.id}`.toLowerCase().includes(search.toLowerCase())
  );

  const selected = options.find(o => o.id === value);

  return (
    <div className="pm-multiselect" ref={ref} style={{ zIndex: open ? 10 : 1 }}>
      <div className="pm-multiselect-trigger" onClick={() => setOpen(o => !o)}>
        <div className="pm-multiselect-values">
          {selected
            ? (
              <span
                className="pm-tag"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                onMouseEnter={(e) => {
                  const icon = e.currentTarget.querySelector('.pm-tag-remove-icon');
                  if (icon) icon.style.display = 'inline-flex';
                }}
                onMouseLeave={(e) => {
                  const icon = e.currentTarget.querySelector('.pm-tag-remove-icon');
                  if (icon) icon.style.display = 'none';
                }}
              >
                {selected.label}
                <span
                  className="pm-tag-remove-icon"
                  style={{ display: 'none', cursor: 'pointer', alignItems: 'center', color: '#64748b' }}
                  onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
                >
                  <FiX size={12} />
                </span>
              </span>
            )
            : <span className="pm-placeholder">{placeholder}</span>
          }
        </div>
        {open ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
      </div>

      {open && (
        <div className="pm-multiselect-dropdown" style={{ zIndex: 999, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
          <div className="pm-multiselect-search" style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiSearch size={13} style={{ color: '#94a3b8' }} />
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." style={{ border: 'none', outline: 'none', width: '100%', fontSize: '13px' }} />
          </div>
          <div className="pm-multiselect-list" style={{ maxHeight: '200px', overflowY: 'auto', padding: '4px 0' }}>
            {value && (
              <label className="pm-multiselect-item" onClick={() => { onChange(""); setOpen(false); }}>
                <span className="pm-emp-name" style={{ color: "#94a3b8", fontStyle: "italic" }}>— Clear selection —</span>
              </label>
            )}
            {filtered.map(o => (
              <label
                key={o.id}
                className={`pm-multiselect-item ${value === o.id ? "checked" : ""}`}
                onClick={() => { onChange(o.id); setOpen(false); }}
              >
                <div className="pm-emp-info">
                  <span className="pm-emp-name">{o.label}</span>
                </div>
                {value === o.id && <FiCheckSquare size={14} className="pm-check-icon" />}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Delete Confirm ─── */
function DeleteModal({ project, onClose, onConfirm }) {
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/projects/${project.id}`);
      toast.success("Project deleted");
      onConfirm();
    } catch {
      toast.error("Failed to delete project");
    } finally {
      setDeleting(false);
    }
  };
  return (
    <div className="pm-modal-backdrop" onClick={onClose}>
      <div className="pm-modal pm-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="pm-modal-header">
          <h2 className="pm-modal-title">Delete Project</h2>
          <button className="pm-modal-close" onClick={onClose}><FiX size={18} /></button>
        </div>
        <div className="pm-modal-body">
          <p className="pm-delete-msg">
            Are you sure you want to delete <strong>{project.project_name}</strong>?
            This action cannot be undone.
          </p>
        </div>
        <div className="pm-modal-footer">
          <button className="pm-btn pm-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="pm-btn pm-btn-danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Status Badge ─── */
function StatusBadge({ status, projectStatuses = [] }) {
  const cfg = getStatusConfig(status, projectStatuses);
  return (
    <span className="pm-status-badge" style={{ background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

/* ─── Filter Popover ─── */
function FilterPopover({ filters, onApply, onReset, employees, projectStatuses = [] }) {
  const [local, setLocal] = useState({ ...filters });
  const ref = useRef(null);

  const safeEmployees = Array.isArray(employees) ? employees : [];
  const empOptions = safeEmployees.map(e => {
    const eid = e.employee_id || e.employeeId || e.id || e.code;
    const fname = e.first_name || e.firstName || "";
    const lname = e.last_name || e.lastName || "";
    const name = e.name || e.displayName || e.full_name || `${fname} ${lname}`.trim() || String(eid);
    const desigObj = designations.find(d => String(d.designation_id) === String(e.designation) || String(d.id) === String(e.designation) || String(d.designation_name) === String(e.designation));
    const desigName = desigObj ? desigObj.designation_name : e.designation;
    const designationStr = desigName ? ` (${desigName})` : "";
    return { id: eid, label: name + designationStr };
  });

  const set = (key, val) => setLocal(f => ({ ...f, [key]: val }));

  return (
    <div className="pm-filter-popover" ref={ref}>
      <div className="pm-filter-popover-header">
        <FiFilter size={14} />
        <span>Filter Projects</span>
      </div>
      <div className="pm-filter-popover-body">
        <div className="pm-filter-row">
          <label className="pm-label">Status</label>
          <select className="pm-input pm-select" value={local.status} onChange={e => set("status", e.target.value)}>
            <option value="">All Statuses</option>
            {getDynamicStatuses(projectStatuses).map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="pm-filter-row">
          <label className="pm-label">Project Owner</label>
          <SingleSelect
            options={empOptions}
            value={local.projectOwner}
            onChange={v => set("projectOwner", v)}
            placeholder="All owners"
          />
        </div>
        <div className="pm-filter-row">
          <label className="pm-label">Technical Lead</label>
          <SingleSelect
            options={empOptions}
            value={local.technicalLead}
            onChange={v => set("technicalLead", v)}
            placeholder="All leads"
          />
        </div>
        <div className="pm-filter-row">
          <label className="pm-label"><FiCalendar size={12} /> Start Date From</label>
          <input type="date" className="pm-input" value={local.startDateFrom || ""} onChange={e => set("startDateFrom", e.target.value)} />
        </div>
        <div className="pm-filter-row">
          <label className="pm-label"><FiCalendar size={12} /> Start Date To</label>
          <input type="date" className="pm-input" value={local.startDateTo || ""} onChange={e => set("startDateTo", e.target.value)} />
        </div>
      </div>
      <div className="pm-filter-popover-footer">
        <button className="pm-btn pm-btn-cancel pm-btn-sm" onClick={() => onReset()}>
          <FiRefreshCw size={13} /> Reset
        </button>
        <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={() => onApply(local)}>
          Apply
        </button>
      </div>
    </div>
  );
}

/* ─── Project Details View (Read Only) ─── */
function ProjectDetailsView({ project, onBack, onEdit, onDelete, employees, clients, priorities, user, projectStatuses = [] }) {
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [activeRightTab, setActiveRightTab] = useState("timeline");

  useEffect(() => {
    if (project?.id) {
      setTimelineLoading(true);
      api.get(`/api/projects/${project.id}/timeline`)
        .then(res => setTimeline(res.data.data || []))
        .catch(() => setTimeline([]))
        .finally(() => setTimelineLoading(false));
    }
  }, [project?.id]);

  const priorityName = (id) => priorities.find(p => String(p.id) === String(id))?.priority_name || "—";
  const clientDisplayName = (id) => clients.find(c => String(c.id) === String(id))?.client_name || "—";
  const empName = (id) => {
    if (!id) return "—";
    if (typeof id === 'string' && id.includes(',')) {
      return id.split(',').map(i => empName(i.trim())).join(', ');
    }
    const e = (Array.isArray(employees) ? employees : []).find(emp => String(emp.employee_id) === String(id) || String(emp.id) === String(id));
    return e ? (e.name || e.displayName || e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim()) : id;
  };

  const empRole = (id) => {
    if (!id || id === 'system') return "";
    const e = (Array.isArray(employees) ? employees : []).find(emp => String(emp.employee_id) === String(id) || String(emp.id) === String(id));
    return e?.designation ? ` - ${e.designation}` : e?.role ? ` - ${e.role}` : "";
  };

  const statusConfig = getStatusConfig(project.status || "Pending", projectStatuses);

  const InfoRow = ({ label, value }) => (
    <div style={{ display: 'flex', flexDirection: 'column', borderBottom: '1px solid #f1f5f9', padding: '10px 0', gap: '4px' }}>
      <div style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>{label}</div>
      <div style={{ color: '#0f172a', fontSize: 14 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ paddingBottom: 40, width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Actions Header matches Observation screen */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, padding: '12px 24px', background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', color: '#64748b', transition: 'all 0.2s', padding: 0 }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>←</span>
          </button>
          <h2 style={{ margin: 0, fontSize: 16, color: '#0f172a', fontWeight: 600 }}>
            Project #{project.sfprojectsid || project.id}
          </h2>
          <span style={{ padding: '3px 10px', borderRadius: 4, background: statusConfig.bg, color: statusConfig.color, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{statusConfig.label}</span>
          <span style={{ padding: '3px 10px', borderRadius: 4, background: '#ecfdf5', color: '#10b981', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{priorityName(project.priority_id)}</span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {(String(user?.role || "").toUpperCase() === "ADMIN" || String(user?.role || "").toUpperCase() === "MANAGER") && (
            <>
              <button onClick={onEdit} className="pm-btn" style={{ background: '#fff', color: '#4F6EF7', border: '1px solid #4F6EF7' }}>
                <FiEdit2 size={13} style={{ marginRight: 6 }} /> Edit
              </button>
              <button onClick={onDelete} className="pm-btn pm-btn-danger">
                <FiTrash2 size={13} style={{ marginRight: 6 }} /> Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Layout Wrapper: 2 Column Top (Properties + Timeline), Documents Full Width Bottom */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

        {/* Top Section */}
        <div style={{ display: 'flex', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', minHeight: 450 }}>

          {/* Left Column: Properties */}
          <div style={{ flex: 1, padding: '24px 32px', borderRight: '1px solid #e2e8f0' }}>
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#0f172a' }}>{project.project_name || "Untitled Project"}</h2>
              <div style={{ fontSize: 13, color: '#64748b' }}>Client: {clientDisplayName(project.client_master_id)}</div>
            </div>

            {/* 1 Field 2 Columns grid layout */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px' }}>
              <InfoRow label="Project Name" value={project.project_name || "—"} />
              <InfoRow label="Client Name" value={clientDisplayName(project.client_master_id)} />
              <InfoRow label="Priority" value={priorityName(project.priority_id)} />
              <InfoRow label="Phase" value={project.phase || "—"} />
              <InfoRow label="Start Date" value={fmtDateOnly(project.start_date)} />
              <InfoRow label="End Date" value={fmtDateOnly(project.end_date)} />
              <InfoRow label="Due Date" value={fmtDateOnly(project.due_date)} />
              <InfoRow label="Project Owner" value={empName(project.project_owner)} />
              <InfoRow label="Technical Lead" value={empName(project.technical_lead)} />
              <InfoRow label="Project Manager" value={empName(project.project_manager)} />
              <InfoRow label="Developers" value={Array.isArray(project.developers) ? project.developers.map(empName).join(', ') : empName(project.developers)} />
              <InfoRow label="Created By" value={
                project.created_by
                  ? `${empName(project.created_by)} (${project.created_by})${empRole(project.created_by)}`
                  : user
                    ? `${user.name || user.fullName || 'User'} (${user.empId || user.employeeId || ''}) - ${user.designation || user.role || 'User'}`
                    : "—"
              } />
              <InfoRow label="Created At" value={fmtDateTime(project.created_at || project.createdAt)} />
              <InfoRow label="Updated By" value={
                project.updated_by
                  ? `${empName(project.updated_by)} (${project.updated_by})${empRole(project.updated_by)}`
                  : project.created_by
                    ? `${empName(project.created_by)} (${project.created_by})${empRole(project.created_by)}`
                    : user
                      ? `${user.name || user.fullName || 'User'} (${user.empId || user.employeeId || ''}) - ${user.designation || user.role || 'User'}`
                      : "—"
              } />
              <InfoRow label="Updated At" value={fmtDateTime(project.updated_at || project.updatedAt || project.created_at || project.createdAt)} />
              <div style={{ gridColumn: '1 / -1' }}>
                <InfoRow label="Notification Members" value={Array.isArray(project.notification_members) ? project.notification_members.map(empName).join(', ') : empName(project.notification_members)} />
              </div>
            </div>

            <div style={{ marginTop: 24, paddingTop: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiTag size={14} /> Description
              </div>
              <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.6 }}>{project.description || "—"}</div>
            </div>

            <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FiFileText size={14} /> Notes
              </div>
              <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.6 }}>
                {project.notes ? <div dangerouslySetInnerHTML={{ __html: project.notes }} /> : "—"}
              </div>
            </div>
          </div>

          {/* Right Column: Timeline */}
          <div style={{ width: '320px', flexShrink: 0, padding: '24px', background: '#fafbff', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 250px)' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#10b981', marginBottom: 24, paddingBottom: 12, borderBottom: '2px solid #10b981', display: 'inline-block' }}>
                Status Timeline
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '8px' }}>
              {timelineLoading ? (
                <div className="pm-loading" style={{ padding: '40px 0' }}><div className="pm-spinner" /></div>
              ) : timeline.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>No timeline events yet.</div>
              ) : (
                <div style={{ position: 'relative', paddingLeft: 20 }}>
                  <div style={{ position: 'absolute', left: 5, top: 0, bottom: 0, width: 2, background: '#e2e8f0' }} />
                  {timeline.map((t, i) => {
                    const cfg = t.action === 'Created'
                      ? { color: '#10b981', bg: '#ecfdf5' }
                      : t.action === 'Status Changed'
                        ? { color: '#f59e0b', bg: '#fffbeb' }
                        : { color: '#4F6EF7', bg: '#eff6ff' };
                    return (
                      <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20, position: 'relative' }}>
                        <div style={{ width: 12, height: 12, borderRadius: '50%', background: cfg.bg, border: `2px solid ${cfg.color}`, flexShrink: 0, zIndex: 1, marginTop: 4 }} />
                        <div style={{ flex: 1, background: '#fff', borderRadius: 8, padding: '10px 14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                          <div style={{ fontWeight: 700, fontSize: 12, color: cfg.color }}>{t.action}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 6px' }}>{fmtDateTime(t.changed_at)}</div>
                          {t.action === 'Status Changed' && (
                            <div style={{ fontSize: 12, color: '#475569' }}>
                              <span style={{ background: '#fef2f2', color: '#ef4444', borderRadius: 4, padding: '2px 4px', fontWeight: 600 }}>{t.old_value}</span>
                              <span style={{ margin: '0 4px', color: '#94a3b8' }}>→</span>
                              <span style={{ background: '#f0fdf4', color: '#10b981', borderRadius: 4, padding: '2px 4px', fontWeight: 600 }}>{t.new_value}</span>
                            </div>
                          )}
                          {t.action !== 'Status Changed' && t.new_value && (
                            <div style={{ fontSize: 12, color: '#475569' }}>
                              Status: <span style={{ fontWeight: 600, color: '#4F6EF7' }}>{t.new_value}</span>
                            </div>
                          )}
                          {t.changed_by && (
                            <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>
                              By: {(() => {
                                if (!t.changed_by || t.changed_by === 'system') return 'System';
                                return empName(t.changed_by);
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section: Documents */}
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: '24px 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
            <FiPaperclip size={16} style={{ color: '#3b82f6' }} /> Documents
          </div>
          <ProjectDocumentsTab project={project} readOnly={true} />
        </div>
      </div>
    </div>
  );
}

// ── Dropdown Component ────────────────────────────────────────────────────────
function Dropdown({ trigger, items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  return (
    <div className="em-dropdown" ref={ref}>
      <div onClick={() => setOpen(o => !o)}>{trigger}</div>
      {open && (
        <div className="em-dropdown-menu">
          {items.map((item, i) => (
            <div key={i} className="em-dropdown-item" onClick={() => { item.onClick(); setOpen(false); }}>
              {item.icon}{item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Main Page ─── */
export default function ProjectMaster() {
  const { user, logout } = useAuth();
  const canManage = String(user?.role || "").toUpperCase() === "ADMIN" || String(user?.role || "").toUpperCase() === "MANAGER";
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("list");
  const [displayMode, setDisplayMode] = useState("kanban"); // "table" | "kanban"
  const [initialFormTab, setInitialFormTab] = useState("details");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // New GroupBy & Custom Date filter State
  const [kanbanGroupBy, setKanbanGroupBy] = useState("status"); // "status" | "client" | "owner"
  const [showGroupByDropdown, setShowGroupByDropdown] = useState(false);
  const groupByRef = useRef(null);

  // Advanced Date Range Filter State
  const [dateRangeFilter, setDateRangeFilter] = useState({ label: '', from: '', to: '' });
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const dateFiltRef = useRef(null);

  // Column Visibility & Export
  const [showColDropdown, setShowColDropdown] = useState(false);
  const colRef = useRef(null);
  const [visibleColumns, setVisibleColumns] = useState({
    projectId: true, projectName: true, clientName: true, startDate: true, endDate: true, dueDate: true, phase: true, status: true, owner: true, techLead: true
  });

  // Filter drawer + popover state
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const filterRef = useRef(null);
  const [advancedFilters, setAdvancedFilters] = useState({
    status: "",
    projectOwner: "",
    technicalLead: "",
    startDateFrom: "",
    startDateTo: "",
  });
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // Close filter popover on outside click
  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilterPopover(false);
      if (groupByRef.current && !groupByRef.current.contains(e.target)) setShowGroupByDropdown(false);
      if (colRef.current && !colRef.current.contains(e.target)) setShowColDropdown(false);
      if (dateFiltRef.current && !dateFiltRef.current.contains(e.target)) setShowDateDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleDatePreset = (preset) => {
    let from = '', to = '';
    const today = new Date();
    const toYMD = (d) => {
      const offset = d.getTimezoneOffset() * 60000;
      return new Date(d.getTime() - offset).toISOString().split('T')[0];
    };

    if (preset === 'Today') {
      from = to = toYMD(today);
    } else if (preset === 'Last 7 days') {
      const past = new Date(today); past.setDate(today.getDate() - 6);
      from = toYMD(past); to = toYMD(today);
    } else if (preset === 'Last 14 days') {
      const past = new Date(today); past.setDate(today.getDate() - 13);
      from = toYMD(past); to = toYMD(today);
    } else if (preset === 'Last 30 days') {
      const past = new Date(today); past.setDate(today.getDate() - 29);
      from = toYMD(past); to = toYMD(today);
    } else if (preset === 'Last 3 months') {
      const past = new Date(today); past.setMonth(today.getMonth() - 2);
      from = toYMD(past); to = toYMD(today);
    } else if (preset === 'Last 12 months') {
      const past = new Date(today); past.setFullYear(today.getFullYear() - 1);
      from = toYMD(past); to = toYMD(today);
    } else if (preset === 'Custom') {
      setDateRangeFilter({ label: 'Custom', from: dateRangeFilter.from, to: dateRangeFilter.to });
      return;
    }
    setDateRangeFilter({ label: preset, from, to });
    if (preset !== 'Custom') setShowDateDropdown(false);
    setPage(1);
  };

  // Fetch employees for dropdowns
  const fetchEmployees = useCallback(async () => {
    try {
      const res = await api.get("/api/employee-master?page=1&limit=9999");
      // res.data is the axios response body
      const body = res.data;

      // Look for the array in various common places
      let raw = [];
      if (Array.isArray(body?.data)) raw = body.data;
      else if (Array.isArray(body?.data?.employees)) raw = body.data.employees;
      else if (Array.isArray(body?.employees)) raw = body.employees;
      else if (Array.isArray(body)) raw = body;

      setEmployees(raw);
    } catch (err) {
      console.warn("Could not load employees:", err.message);
      setEmployees([]);
    }
  }, []);

  const fetchMasterData = useCallback(async () => {
    try {
      const [cRes, pRes, sRes, dRes] = await Promise.all([
        api.get("/api/client-master").catch(() => ({ data: { data: [] } })),
        api.get("/api/priority-master").catch(() => ({ data: { data: [] } })),
        api.get("/api/project-status-master").catch(() => ({ data: { data: [] } })),
        api.get("/api/designation-master").catch(() => ({ data: { data: [] } }))
      ]);
      setClients(cRes?.data?.data || []);
      setPriorities(pRes?.data?.data || []);
      setProjectStatuses(sRes?.data?.data || []);
      setDesignations(dRes?.data?.data || []);
    } catch (err) { console.warn(err); }
  }, []);

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all projects matching filters for accurate Kanban counts, pagination handled client-side
      const params = new URLSearchParams({ limit: 99999 });
      if (search) params.set("search", search);
      if (statusFilter !== "All") params.set("status", statusFilter);
      const res = await api.get(`/api/projects?${params}`);
      setProjects(res.data?.data?.projects || res.data?.projects || res.data?.data || []);
    } catch {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchEmployees(); fetchMasterData(); }, [fetchEmployees, fetchMasterData]);
  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Helpers to find employee name from ID
  const empName = (id) => {
    if (!id) return "—";
    const emp = employees.find(e => (e.employee_id || e.employeeId || e.id || e.code) === id);
    if (!emp) return id;
    const fname = emp.first_name || emp.firstName || "";
    const lname = emp.last_name || emp.lastName || "";
    const name = emp.name || emp.displayName || emp.full_name;
    return name || `${fname} ${lname}`.trim() || id;
  };

  const empNamesArr = (ids = []) => ids.map(empName).join(", ") || "—";
  const priorityName = (id) => {
    const p = priorities.find(p => String(p.id) === String(id));
    return p ? p.priority_name : '—';
  };

  const exportProjectsCSV = () => {
    if (projects.length === 0) return toast.info("No projects to export.");
    const csvData = displayProjects.map(p => ({
      "Project ID": p.sfprojectsid || `PRJ-${p.id}`,
      "Project Name": p.project_name || "",
      "Client Name": p.client_name || "",
      "Start Date": p.start_date ? p.start_date.split("T")[0] : "",
      "End Date": p.end_date ? p.end_date.split("T")[0] : "",
      "Due Date": p.due_date ? p.due_date.split("T")[0] : "",
      "Status": p.status || "",
      "Project Owner": empName(p.project_owner),
      "Technical Lead": empName(p.technical_lead),
    }));

    const exportMap = {
      "Project ID": visibleColumns.projectId, "Project Name": visibleColumns.projectName,
      "Client Name": visibleColumns.clientName, "Start Date": visibleColumns.startDate,
      "End Date": visibleColumns.endDate, "Due Date": visibleColumns.dueDate,
      "Status": visibleColumns.status, "Project Owner": visibleColumns.owner,
      "Technical Lead": visibleColumns.techLead
    };

    const headers = Object.keys(exportMap).filter(k => exportMap[k]);
    if (headers.length === 0) return toast.info("No visible columns to export.");

    const titleRow = `"Project Master Report"${','.repeat(headers.length - 1)}`;
    const csvRows = [titleRow, headers.join(',')];
    for (const row of csvData) {
      csvRows.push(headers.map(h => `"${(row[h] || "").toString().replace(/"/g, '""')}"`).join(','));
    }

    const blob = new Blob([csvRows.join('\\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Projects_Export_${fmtDateOnly(new Date())}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const closeModal = (refresh = false) => {
    setModal(null);
    setSelected(null);
    if (refresh) {
      setView("list");
      fetchProjects();
    }
  };

  // Apply advanced filters client-side
  const getFilteredProjects = () => {
    let filtered = [...projects];

    if (dateRangeFilter.from) {
      filtered = filtered.filter(p => p.start_date && p.start_date.split("T")[0] >= dateRangeFilter.from);
    }
    if (dateRangeFilter.to) {
      filtered = filtered.filter(p => p.start_date && p.start_date.split("T")[0] <= dateRangeFilter.to);
    }

    if (advancedFilters.projectOwner) {
      filtered = filtered.filter(p => String(p.project_owner) === String(advancedFilters.projectOwner));
    }
    if (advancedFilters.technicalLead) {
      filtered = filtered.filter(p => String(p.technical_lead) === String(advancedFilters.technicalLead));
    }
    if (advancedFilters.startDateFrom) {
      filtered = filtered.filter(p => p.start_date && p.start_date.split("T")[0] >= advancedFilters.startDateFrom);
    }
    if (advancedFilters.startDateTo) {
      filtered = filtered.filter(p => p.start_date && p.start_date.split("T")[0] <= advancedFilters.startDateTo);
    }

    return filtered;
  };

  const handleApplyFilters = (filters) => {
    // Status filter goes to API-level filter
    if (filters.status) {
      setStatusFilter(filters.status);
    } else {
      setStatusFilter("All");
    }
    setAdvancedFilters(filters);

    // Count active filters
    let count = 0;
    if (filters.status) count++;
    if (filters.projectOwner) count++;
    if (filters.technicalLead) count++;
    if (filters.startDateFrom) count++;
    if (filters.startDateTo) count++;
    setActiveFilterCount(count);

    setShowFilterPopover(false);
    setPage(1);
  };

  const handleResetFilters = () => {
    const empty = { status: "", projectOwner: "", technicalLead: "", startDateFrom: "", startDateTo: "" };
    setAdvancedFilters(empty);
    setStatusFilter("All");
    setActiveFilterCount(0);
    setShowFilterPopover(false);
    setPage(1);
  };

  const handleKanbanDrop = async (projectId, newStatusId) => {
    const p = projects.find(proj => String(proj.id) === String(projectId));
    if (!p) return;
    if (String(p.status) === String(newStatusId)) return;

    const previousStatus = p.status;
    setProjects(prev => prev.map(proj => String(proj.id) === String(projectId) ? { ...proj, status: newStatusId } : proj));

    try {
      const currentUserName = getUserDisplayName(user);
      const payload = {
        projectName: p.project_name,
        description: p.description,
        startDate: p.start_date || null,
        endDate: p.end_date || null,
        dueDate: p.due_date || null,
        clientName: p.client_name || p.client_master_id || null,
        clientMasterId: p.client_master_id ? parseInt(p.client_master_id) : null,
        priorityId: p.priority_id ? parseInt(p.priority_id) : null,
        notes: p.notes || null,
        status: newStatusId,
        developers: Array.isArray(p.developers) ? p.developers : (p.developers ? p.developers.split(',') : []),
        projectOwner: p.project_owner || null,
        technicalLead: p.technical_lead || null,
        projectManager: p.project_manager || null,
        notificationMembers: Array.isArray(p.notification_members) ? p.notification_members : (p.notification_members ? p.notification_members.split(',') : []),
        sfprojectsid: p.sfprojectsid || null,
        updatedBy: currentUserName,
        updated_by: currentUserName,
        changedBy: currentUserName,
        changed_by: currentUserName,
      };
      await api.put(`/api/projects/${projectId}`, payload);
      toast.success("Project status updated successfully");
    } catch (err) {
      toast.error("Failed to update project status");
      setProjects(prev => prev.map(proj => String(proj.id) === String(projectId) ? { ...proj, status: previousStatus } : proj));
    }
  };

  const displayProjects = getFilteredProjects();
  const total = displayProjects.length;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const paginatedProjects = displayMode === 'table'
    ? displayProjects.slice((page - 1) * rowsPerPage, page * rowsPerPage)
    : displayProjects;

  /* ─── Kanban grouped data ─── */

  const buildKanbanColumns = (listToGroup) => {
    if (kanbanGroupBy === "status") {
      return getDynamicStatuses(projectStatuses).map(s => {
        const sVal = s.value.toLowerCase().replace(/\s+/g, '');
        const sLab = s.label.toLowerCase().replace(/\s+/g, '');
        return {
          ...s,
          projects: listToGroup.filter(p => {
            const pStat = String(p.status || "").toLowerCase().replace(/\s+/g, '');
            return pStat === sVal || pStat === sLab || p.status === s.value || p.status === s.label;
          })
        };
      });
    } else if (kanbanGroupBy === "client") {
      const clientMap = {};
      listToGroup.forEach(p => {
        const client = p.client_name || "Unassigned";
        if (!clientMap[client]) clientMap[client] = [];
        clientMap[client].push(p);
      });
      return Object.keys(clientMap).map(c => ({
        label: c,
        value: c,
        color: "#6366f1",
        bg: "#eef2ff",
        projects: clientMap[c]
      }));
    } else if (kanbanGroupBy === "owner") {
      const ownerMap = {};
      listToGroup.forEach(p => {
        const owner = empName(p.project_owner);
        if (!ownerMap[owner]) ownerMap[owner] = [];
        ownerMap[owner].push(p);
      });
      return Object.keys(ownerMap).map(o => ({
        label: o,
        value: o,
        color: "#0f766e",
        bg: "#ccfbf1",
        projects: ownerMap[o]
      }));
    } else if (kanbanGroupBy === "technicalLead") {
      const map = {};
      listToGroup.forEach(p => {
        const tl = empName(p.technical_lead);
        if (!map[tl]) map[tl] = [];
        map[tl].push(p);
      });
      return Object.keys(map).map(k => ({ label: k, color: "#9333ea", bg: "#faf5ff", projects: map[k] }));
    } else if (kanbanGroupBy === "project") {
      const map = {};
      listToGroup.forEach(p => {
        const title = p.project_name || "Unknown";
        if (!map[title]) map[title] = [];
        map[title].push(p);
      });
      return Object.keys(map).map(k => ({ label: k, color: "#1d4ed8", bg: "#eff6ff", projects: map[k] }));
    } else if (kanbanGroupBy === "developers") {
      const map = {};
      listToGroup.forEach(p => {
        const devs = Array.isArray(p.developers) ? p.developers : (p.developers ? p.developers.split(',') : []);
        if (devs.length === 0) {
          if (!map["Unassigned"]) map["Unassigned"] = [];
          map["Unassigned"].push(p);
        } else {
          devs.forEach(d => {
            const k = empName(d.trim());
            if (!map[k]) map[k] = [];
            if (!map[k].find(existing => existing.id === p.id)) map[k].push(p);
          });
        }
      });
      return Object.keys(map).map(k => ({ label: k, color: "#0ea5e9", bg: "#f0f9ff", projects: map[k] }));
    } else if (kanbanGroupBy === "none") {
      return [{ label: "All Projects", value: "none", color: "#475569", bg: "#f1f5f9", projects: listToGroup }];
    }
    return [{ label: "All Projects", value: "all", color: "#475569", bg: "#f1f5f9", projects: listToGroup }];
  };

  const kanbanColumns = buildKanbanColumns(displayMode === 'table' ? paginatedProjects : displayProjects);

  /* ─── Filter Drawer (slide-over panel) ─── */
  const renderFilterDrawer = () => {
    if (!showFilterDrawer) return null;
    return (
      <>
        <div className="pm-drawer-backdrop" onClick={() => setShowFilterDrawer(false)} />
        <div className="pm-drawer">
          <div className="pm-drawer-header">
            <span>Filter</span>
            <button className="pm-modal-close" onClick={() => setShowFilterDrawer(false)}><FiX size={18} /></button>
          </div>
          <div className="pm-drawer-body">
            <div className="pm-filter-row">
              <label className="pm-label">Status</label>
              <select className="pm-input pm-select" value={advancedFilters.status} onChange={e => setAdvancedFilters(f => ({ ...f, status: e.target.value }))}>
                <option value="">All Statuses</option>
                {getDynamicStatuses(projectStatuses).map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="pm-filter-row">
              <label className="pm-label">Project Owner</label>
              <select className="pm-input pm-select" value={advancedFilters.projectOwner} onChange={e => setAdvancedFilters(f => ({ ...f, projectOwner: e.target.value }))}>
                <option value="">Select Owner</option>
                {(Array.isArray(employees) ? employees : []).map(e => {
                  const eid = e.employee_id || e.employeeId || e.id || e.code;
                  const name = e.name || e.displayName || e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || String(eid);
                  return <option key={eid} value={eid}>{name}</option>;
                })}
              </select>
            </div>
            <div className="pm-filter-row">
              <label className="pm-label">Technical Lead</label>
              <select className="pm-input pm-select" value={advancedFilters.technicalLead} onChange={e => setAdvancedFilters(f => ({ ...f, technicalLead: e.target.value }))}>
                <option value="">Select Lead</option>
                {(Array.isArray(employees) ? employees : []).map(e => {
                  const eid = e.employee_id || e.employeeId || e.id || e.code;
                  const name = e.name || e.displayName || e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || String(eid);
                  return <option key={eid} value={eid}>{name}</option>;
                })}
              </select>
            </div>
            <div className="pm-filter-row">
              <label className="pm-label"><FiCalendar size={12} /> Start Date From</label>
              <input type="date" className="pm-input" value={advancedFilters.startDateFrom || ""} onChange={e => setAdvancedFilters(f => ({ ...f, startDateFrom: e.target.value }))} />
            </div>
            <div className="pm-filter-row">
              <label className="pm-label"><FiCalendar size={12} /> Start Date To</label>
              <input type="date" className="pm-input" value={advancedFilters.startDateTo || ""} onChange={e => setAdvancedFilters(f => ({ ...f, startDateTo: e.target.value }))} />
            </div>
          </div>
          <div className="pm-drawer-footer">
            <button className="pm-btn pm-btn-cancel" onClick={() => { handleResetFilters(); setShowFilterDrawer(false); }}>
              <FiRefreshCw size={13} /> Reset
            </button>
            <button className="pm-btn pm-btn-cancel" onClick={() => setShowFilterDrawer(false)}>Cancel</button>
            <button className="pm-btn pm-btn-primary" onClick={() => { handleApplyFilters(advancedFilters); setShowFilterDrawer(false); }}>Apply</button>
          </div>
        </div>
      </>
    );
  };

  return (
    <MainLayout sidebarOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(p => !p)} title="Project Master">
      {/* <TopNavBar
        user={user}
        notifications={0}
        toggleSidebar={() => setSidebarOpen(p => !p)}
        logout={logout}
      /> */}

      <div className="pm-page" style={{ padding: 0 }}>
        {view === "list" ? (
          <>
            {/* Filters Bar */}
            <div className="pm-filters" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'transparent', padding: '0 0 16px 0', borderBottom: 'none' }}>
              <div className="pm-search-wrap" style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', minWidth: '220px' }}>
                <FiSearch size={15} style={{ color: '#64748b', marginRight: '8px' }} />
                <input
                  className="pm-search-input"
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '13.5px', color: '#1e293b', width: '100%' }}
                  placeholder="Search here..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                />
                {search && <button className="pm-search-clear" onClick={() => setSearch("")} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8' }}><FiX size={13} /></button>}
              </div>

              {/* dd-mm-yyyy Date Picker Dropdown */}
              <div className="pm-date-filter-wrap" ref={dateFiltRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <button
                  className="pm-btn-groupby-action"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '8px', minHeight: '36px',
                    background: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: '13.5px', color: '#475569', fontWeight: 500, minWidth: '140px', justifyContent: 'space-between'
                  }}
                  onClick={() => setShowDateDropdown(!showDateDropdown)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{dateRangeFilter.label || "dd-mm-yyyy"}</span>
                  </div>
                  {dateRangeFilter.label ? (
                    <FiX size={14} style={{ color: '#94a3b8' }} onClick={(e) => { e.stopPropagation(); setDateRangeFilter({ label: '', from: '', to: '' }); setPage(1); }} />
                  ) : (
                    <FiCalendar size={14} style={{ color: '#94a3b8' }} />
                  )}
                </button>
                {showDateDropdown && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 100, display: 'flex', padding: 0
                  }}>
                    {/* Presets Column */}
                    <div style={{ width: '150px', borderRight: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column' }}>
                      {["Today", "Last 7 days", "Last 14 days", "Last 30 days", "Last 3 months", "Last 12 months", "Custom"].map(preset => (
                        <div
                          key={preset}
                          style={{
                            padding: '10px 16px', fontSize: '13px', cursor: 'pointer', borderBottom: '1px solid #f8fafc',
                            background: dateRangeFilter.label === preset ? '#f1f5f9' : 'transparent',
                            color: dateRangeFilter.label === preset ? '#0f172a' : '#475569',
                            fontWeight: dateRangeFilter.label === preset ? 600 : 400
                          }}
                          onClick={() => handleDatePreset(preset)}
                          onMouseEnter={(e) => e.target.style.background = '#f8fafc'}
                          onMouseLeave={(e) => e.target.style.background = dateRangeFilter.label === preset ? '#f1f5f9' : 'transparent'}
                        >
                          {preset}
                        </div>
                      ))}
                    </div>
                    {/* Custom Date Selection (Only shows if Custom is active) */}
                    {dateRangeFilter.label === 'Custom' && (
                      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '180px' }}>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>From Date</div>
                          <input
                            type="date"
                            className="pm-input"
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                            value={dateRangeFilter.from}
                            onChange={(e) => setDateRangeFilter(f => ({ ...f, from: e.target.value }))}
                          />
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>To Date</div>
                          <input
                            type="date"
                            className="pm-input"
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                            value={dateRangeFilter.to}
                            onChange={(e) => setDateRangeFilter(f => ({ ...f, to: e.target.value }))}
                          />
                        </div>
                        <button
                          className="pm-btn pm-btn-primary"
                          style={{ marginTop: '8px', justifyContent: 'center' }}
                          onClick={() => { setShowDateDropdown(false); setPage(1); }}
                        >
                          Apply Filter
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Filter Button */}
              <button
                className={`pm-btn-filter-action ${activeFilterCount > 0 ? "has-filters" : ""}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px',
                  background: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: '13.5px', color: '#475569', fontWeight: 550, transition: 'all 0.15s'
                }}
                onClick={() => setShowFilterDrawer(true)}
              >
                <FiFilter size={15} style={{ color: '#64748b' }} />
                <span>Filter</span>
                {activeFilterCount > 0 && (
                  <span className="pm-filter-badge" style={{ background: '#f59e0b', color: '#fff', borderRadius: '99px', padding: '1px 6px', fontSize: '11px', fontWeight: 700 }}>
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Group By Dropdown Container */}
              <div className="pm-groupby-container" ref={groupByRef} style={{ position: 'relative' }}>
                <button
                  className="pm-btn-groupby-action"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '8px',
                    background: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: '13.5px', color: '#475569', fontWeight: 550, transition: 'all 0.15s'
                  }}
                  onClick={() => setShowGroupByDropdown(!showGroupByDropdown)}
                >
                  <FiLayers size={15} style={{ color: '#64748b' }} />
                  <span>Group by</span>
                </button>
                {showGroupByDropdown && (
                  <div className="pm-groupby-dropdown" style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 100, minWidth: '180px', padding: '4px 0'
                  }}>
                    <div
                      className={`pm-groupby-opt ${kanbanGroupBy === 'project' ? 'active' : ''}`}
                      style={{ padding: '8px 16px', fontSize: '13.5px', color: '#334155', cursor: 'pointer', background: kanbanGroupBy === 'project' ? '#f1f5f9' : 'transparent', fontWeight: kanbanGroupBy === 'project' ? 600 : 400 }}
                      onClick={() => { setKanbanGroupBy('project'); setShowGroupByDropdown(false); }}
                    >
                      Project Name
                    </div>
                    <div
                      className={`pm-groupby-opt ${kanbanGroupBy === 'client' ? 'active' : ''}`}
                      style={{ padding: '8px 16px', fontSize: '13.5px', color: '#334155', cursor: 'pointer', background: kanbanGroupBy === 'client' ? '#f1f5f9' : 'transparent', fontWeight: kanbanGroupBy === 'client' ? 600 : 400 }}
                      onClick={() => { setKanbanGroupBy('client'); setShowGroupByDropdown(false); }}
                    >
                      Client Name
                    </div>
                    <div
                      className={`pm-groupby-opt ${kanbanGroupBy === 'owner' ? 'active' : ''}`}
                      style={{ padding: '8px 16px', fontSize: '13.5px', color: '#334155', cursor: 'pointer', background: kanbanGroupBy === 'owner' ? '#f1f5f9' : 'transparent', fontWeight: kanbanGroupBy === 'owner' ? 600 : 400 }}
                      onClick={() => { setKanbanGroupBy('owner'); setShowGroupByDropdown(false); }}
                    >
                      Product Owner
                    </div>
                    <div
                      className={`pm-groupby-opt ${kanbanGroupBy === 'technicalLead' ? 'active' : ''}`}
                      style={{ padding: '8px 16px', fontSize: '13.5px', color: '#334155', cursor: 'pointer', background: kanbanGroupBy === 'technicalLead' ? '#f1f5f9' : 'transparent', fontWeight: kanbanGroupBy === 'technicalLead' ? 600 : 400 }}
                      onClick={() => { setKanbanGroupBy('technicalLead'); setShowGroupByDropdown(false); }}
                    >
                      Technical Lead
                    </div>
                    <div
                      className={`pm-groupby-opt ${kanbanGroupBy === 'developers' ? 'active' : ''}`}
                      style={{ padding: '8px 16px', fontSize: '13.5px', color: '#334155', cursor: 'pointer', background: kanbanGroupBy === 'developers' ? '#f1f5f9' : 'transparent', fontWeight: kanbanGroupBy === 'developers' ? 600 : 400 }}
                      onClick={() => { setKanbanGroupBy('developers'); setShowGroupByDropdown(false); }}
                    >
                      Developers
                    </div>
                    <div
                      className={`pm-groupby-opt ${kanbanGroupBy === 'status' ? 'active' : ''}`}
                      style={{ padding: '8px 16px', fontSize: '13.5px', color: '#334155', cursor: 'pointer', background: kanbanGroupBy === 'status' ? '#f1f5f9' : 'transparent', fontWeight: kanbanGroupBy === 'status' ? 600 : 400 }}
                      onClick={() => { setKanbanGroupBy('status'); setShowGroupByDropdown(false); }}
                    >
                      Status
                    </div>
                    <div
                      className={`pm-groupby-opt ${kanbanGroupBy === 'none' ? 'active' : ''}`}
                      style={{ padding: '8px 16px', fontSize: '13.5px', color: '#334155', cursor: 'pointer', background: kanbanGroupBy === 'none' ? '#f1f5f9' : 'transparent', fontWeight: kanbanGroupBy === 'none' ? 600 : 400 }}
                      onClick={() => { setKanbanGroupBy('none'); setShowGroupByDropdown(false); }}
                    >
                      None
                    </div>
                  </div>
                )}
              </div>

              {/* View Switchers + Counts */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
                <div style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '13px', fontWeight: 600, padding: '6px 12px', borderRadius: '6px' }}>
                  {displayProjects.length} Projects
                </div>

                <div className="pm-view-toggle" style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
                  <button
                    className={`pm-view-btn ${displayMode === 'kanban' ? 'active' : ''}`}
                    style={{ border: 'none', padding: '8px 12px', background: displayMode === 'kanban' ? '#f1f5f9' : 'transparent', color: displayMode === 'kanban' ? '#4f46e5' : '#64748b', cursor: 'pointer' }}
                    onClick={() => setDisplayMode('kanban')}
                    title="Kanban View"
                  >
                    <FiGrid size={15} />
                  </button>
                  <button
                    className={`pm-view-btn ${displayMode === 'table' ? 'active' : ''}`}
                    style={{ border: 'none', padding: '8px 12px', background: displayMode === 'table' ? '#f1f5f9' : 'transparent', color: displayMode === 'table' ? '#4f46e5' : '#64748b', cursor: 'pointer', borderLeft: '1px solid #e2e8f0' }}
                    onClick={() => setDisplayMode('table')}
                    title="List View"
                  >
                    <FiList size={15} />
                  </button>
                </div>

                <button
                  className="pm-btn-icon-action"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px',
                    borderRadius: '8px', background: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer', color: '#64748b'
                  }}
                  onClick={exportProjectsCSV}
                  title="Export to CSV"
                >
                  <FiDownload size={15} />
                </button>

                {displayMode === 'table' && (
                  <div style={{ position: 'relative' }} ref={colRef}>
                    <button
                      className="pm-btn-icon-action"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px',
                        borderRadius: '8px', background: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer', color: showColDropdown ? '#4f46e5' : '#64748b'
                      }}
                      onClick={() => setShowColDropdown(!showColDropdown)}
                      title="Show/Hide Columns"
                    >
                      <FiSliders size={15} />
                    </button>
                    {showColDropdown && (
                      <div style={{
                        position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#fff', border: '1px solid #e2e8f0',
                        borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 100, minWidth: '180px', padding: '8px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', marginBottom: '8px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b' }}>Show/Hide Columns</div>
                          <div style={{ fontSize: '11px', fontWeight: 600, color: '#f59e0b', cursor: 'pointer' }} onClick={() => setVisibleColumns({
                            projectId: true, projectName: true, clientName: true, startDate: true, endDate: true, dueDate: true, phase: true, status: true, owner: true, techLead: true
                          })}>Reset</div>
                        </div>
                        {Object.entries({
                          projectId: "Project ID", projectName: "Project Name", clientName: "Client Name",
                          startDate: "Start Date", endDate: "End Date", dueDate: "Due Date", phase: "Phase",
                          status: "Status", owner: "Project Owner", techLead: "Technical Lead"
                        }).map(([key, label]) => (
                          <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', fontSize: '13px', color: '#334155', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={visibleColumns[key]}
                              onChange={(e) => setVisibleColumns(prev => ({ ...prev, [key]: e.target.checked }))}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {canManage && (
                  <button className="pm-btn pm-btn-primary" onClick={() => { setSelected(null); setInitialFormTab("details"); setView("create"); }} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
                    <FiPlus size={14} /> New Project
                  </button>
                )}
              </div>
            </div>

            {/* Content: Table or Kanban */}
            {displayMode === 'kanban' ? (
              /* ─── KANBAN BOARD ─── */
              <div className="pm-kanban-board">
                {loading ? (
                  <div className="pm-loading"><div className="pm-spinner" /><span>Loading projects...</span></div>
                ) : (
                  kanbanColumns.map((col, idx) => (
                    <div key={col.value} className="pm-kanban-col"
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const projectId = e.dataTransfer.getData("projectId");
                        if (projectId && kanbanGroupBy === "status") {
                          handleKanbanDrop(projectId, col.value);
                        }
                      }}>
                      <div className="pm-kanban-col-header" style={{
                        background: col.bg,
                        color: col.color,
                        padding: `12px 20px 12px ${idx === 0 ? '16px' : '24px'}`,
                        clipPath: idx === 0
                          ? 'polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%, 0% 50%)'
                          : 'polygon(0% 0%, calc(100% - 15px) 0%, 100% 50%, calc(100% - 15px) 100%, 0% 100%, 12px 50%)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        borderBottom: 'none'
                      }}>
                        <span className="pm-kanban-col-title" style={{ color: col.color, fontSize: '13.5px', fontWeight: 700 }}>{col.label}</span>
                        <span className="pm-kanban-col-count" style={{ background: '#fff', color: col.color, border: `1px solid ${col.color}`, fontWeight: 700, padding: '2px 8px', borderRadius: '12px', fontSize: '11px', minWidth: '22px', textAlign: 'center' }}>{col.projects.length}</span>
                      </div>
                      <div className="pm-kanban-cards">
                        {col.projects.length === 0 ? (
                          <div className="pm-kanban-empty">No projects</div>
                        ) : (
                          col.projects.map(p => (
                            <div key={p.id} className="pm-kanban-card"
                              draggable={kanbanGroupBy === "status"}
                              onDragStart={(e) => {
                                if (kanbanGroupBy === "status") {
                                  e.dataTransfer.setData("projectId", p.id);
                                  e.dataTransfer.effectAllowed = "move";
                                }
                              }}
                              onClick={() => { setSelected(p); setView("detail"); }}
                              style={{ borderLeft: `4px solid ${col.color || '#6366f1'}`, display: 'flex', flexDirection: 'column', padding: '10px', gap: '6px', cursor: kanbanGroupBy === "status" ? 'grab' : 'pointer' }}>
                              <div className="pm-kc-top" style={{ marginBottom: '4px', alignItems: 'flex-start' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingRight: '4px', flex: 1 }}>
                                  <span className="pm-kc-name" style={{ fontWeight: 'bold', fontSize: '11.5px', color: '#0f172a', lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.project_name || '-'}</span>
                                  <span style={{ fontSize: '10px', color: '#64748b', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.client_name || '-'}</span>
                                </div>
                                <span className="pm-kc-status" style={{ background: col.bg, color: col.color, fontSize: '8px', padding: '2px 4px', marginTop: '2px', flexShrink: 0, borderRadius: '4px' }}>{col.label}</span>
                              </div>

                              {/* Metadata Grid */}
                              <div className="pm-kc-meta-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '4px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span style={{ fontSize: '8px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Created</span>
                                  <span style={{ fontSize: '10px', color: '#334155', fontWeight: 600 }}>{p.created_at ? fmtDateOnly(p.created_at) : p.start_date ? fmtDateOnly(p.start_date) : '—'}</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span style={{ fontSize: '8px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Priority</span>
                                  <span style={{ fontSize: '10px', color: '#334155', fontWeight: 600 }}>{p.priority_id ? priorityName(p.priority_id) : p.priority ? String(p.priority) : '—'}</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span style={{ fontSize: '8px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Start</span>
                                  <span style={{ fontSize: '10px', color: '#334155', fontWeight: 600 }}>{p.start_date ? fmtDateOnly(p.start_date) : '—'}</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span style={{ fontSize: '8px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>End</span>
                                  <span style={{ fontSize: '10px', color: '#dc2626', fontWeight: 600 }}>{p.end_date ? fmtDateOnly(p.end_date) : p.due_date ? fmtDateOnly(p.due_date) : '—'}</span>
                                </div>
                              </div>

                              <div style={{ borderTop: '1px dashed #e2e8f0', margin: '2px 0 4px 0' }} />

                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <span style={{ fontSize: '8px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600 }}>Due Date</span>
                                  <span style={{ fontSize: '10px', color: '#dc2626', fontWeight: 600 }}>{p.due_date ? fmtDateOnly(p.due_date) : '—'}</span>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
                                  {/* Render Developer Icons */}
                                  {(p.developers ? (Array.isArray(p.developers) ? p.developers : p.developers.split(',')) : []).slice(0, 3).map((dev, i) => {
                                    const devName = empName(dev.trim());
                                    const initial = devName !== '—' && devName.length > 0 ? devName.charAt(0).toUpperCase() : '?';
                                    return (
                                      <div key={i} title={devName} style={{
                                        width: '20px', height: '20px', borderRadius: '50%', background: ['#e0e7ff', '#fce7f3', '#dcfce7'][i % 3], color: ['#4f46e5', '#db2777', '#16a34a'][i % 3],
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700,
                                        border: '1px solid #fff', marginLeft: '-4px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                      }}>
                                        {initial}
                                      </div>
                                    )
                                  })}
                                  {p.developers && (Array.isArray(p.developers) ? p.developers : p.developers.split(',')).length > 3 && (
                                    <div style={{
                                      width: '26px', height: '26px', borderRadius: '50%', background: '#f1f5f9', color: '#64748b',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700,
                                      border: '2px solid #fff', marginLeft: '-6px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}>
                                      +{(Array.isArray(p.developers) ? p.developers : p.developers.split(',')).length - 3}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              /* ─── TABLE VIEW ─── */
              <div className="pm-card">
                <div className="pm-table-wrap">
                  {loading ? (
                    <div className="pm-loading">
                      <div className="pm-spinner" />
                      <span>Loading projects...</span>
                    </div>
                  ) : displayProjects.length === 0 ? (
                    <div className="pm-empty">
                      <div className="pm-empty-icon">📁</div>
                      <h3>No projects found</h3>
                      <p>{canManage ? "Create your first project to get started" : "No projects are currently available"}</p>
                      {canManage && (
                        <button className="pm-btn pm-btn-primary" onClick={() => { setSelected(null); setInitialFormTab("details"); setView("create"); }}>
                          <FiPlus size={14} /> Create Project
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                      {kanbanColumns.filter(c => c.projects && c.projects.length > 0).map((col, idx) => (
                        <div key={idx} className="pm-table-group" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                          <div style={{ background: '#f8fafc', padding: '12px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{ fontSize: '14px', fontWeight: 700, color: '#334155' }}>
                                {col.label}
                              </span>
                              <span style={{ background: '#e2e8f0', color: '#475569', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '12px' }}>
                                {col.projects.length}
                              </span>
                            </div>
                          </div>

                          <div style={{ overflowX: 'auto' }}>
                            <table className="pm-table" style={{ margin: 0, border: 'none', borderRadius: 0, boxShadow: 'none' }}>
                              <thead>
                                <tr>
                                  {visibleColumns.projectId && <th>Project ID</th>}
                                  {visibleColumns.projectName && <th>Project Name</th>}
                                  {visibleColumns.clientName && <th>Client Name</th>}
                                  {visibleColumns.startDate && <th>Start Date</th>}
                                  {visibleColumns.endDate && <th>End Date</th>}
                                  {visibleColumns.dueDate && <th>Due Date</th>}
                                  {visibleColumns.phase && <th>Phase</th>}
                                  {visibleColumns.status && <th>Status</th>}
                                  {visibleColumns.owner && <th>Project Owner</th>}
                                  {visibleColumns.techLead && <th>Technical Lead</th>}
                                  <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {col.projects.map(p => (
                                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => { setSelected(p); setView("detail"); }}>
                                    {visibleColumns.projectId && (
                                      <td>
                                        <span className="pm-id">
                                          {p.sfprojectsid || `PRJ-${p.id}`}
                                        </span>
                                      </td>
                                    )}
                                    {visibleColumns.projectName && (
                                      <td className="pm-name-cell">
                                        <strong>{p.project_name}</strong>
                                        {p.description && (
                                          <span className="pm-desc-snippet">
                                            {p.description.length > 50 ? p.description.slice(0, 50) + "…" : p.description}
                                          </span>
                                        )}
                                      </td>
                                    )}
                                    {visibleColumns.clientName && <td className="pm-person">{p.client_name || "—"}</td>}
                                    {visibleColumns.startDate && <td className="pm-date">{fmtDateOnly(p.start_date)}</td>}
                                    {visibleColumns.endDate && <td className="pm-date">{fmtDateOnly(p.end_date)}</td>}
                                    {visibleColumns.dueDate && <td className="pm-date">{fmtDateOnly(p.due_date)}</td>}
                                    {visibleColumns.phase && <td>{p.phase || "—"}</td>}
                                    {visibleColumns.status && <td><StatusBadge status={p.status} projectStatuses={projectStatuses} /></td>}
                                    {visibleColumns.owner && <td className="pm-person">{empName(p.project_owner)}</td>}
                                    {visibleColumns.techLead && <td className="pm-person">{empName(p.technical_lead)}</td>}
                                    <td onClick={e => e.stopPropagation()} style={{ width: '120px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                      <Dropdown
                                        trigger={<button className="em-actions-btn">Actions <FiChevronDown size={13} /></button>}
                                        items={[
                                          { label: "View Details", icon: <FiEye size={14} style={{ marginRight: 8, color: '#64748b' }} />, onClick: () => { setSelected(p); setView("detail"); } },
                                          ...(canManage ? [
                                            { label: "Edit", icon: <FiEdit2 size={14} style={{ marginRight: 8, color: '#64748b' }} />, onClick: () => { setSelected(p); setInitialFormTab("details"); setView("edit"); } },
                                            { label: "Documents", icon: <FiPaperclip size={14} style={{ marginRight: 8, color: '#64748b' }} />, onClick: () => { setSelected(p); setInitialFormTab("documents"); setView("edit"); } },
                                            { label: "Delete", icon: <FiTrash2 size={14} style={{ marginRight: 8, color: '#ef4444' }} />, onClick: () => { setSelected(p); setModal("delete"); } }
                                          ] : []),
                                        ]}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pagination (table view only) */}
            {displayMode === 'table' && total > 0 && (
              <div className="em-pagination" style={{ marginTop: '20px' }}>
                <div className="em-pagination__left">
                  <span className="em-pagination__info">
                    Showing {Math.min((page - 1) * rowsPerPage + 1, total)}–{Math.min(page * rowsPerPage, total)} of {total}
                  </span>
                  <div className="em-rows-selector">
                    <span className="em-rows-label">Rows:</span>
                    <select
                      className="em-rows-select"
                      value={rowsPerPage}
                      onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
                <div className="em-pagination__btns">
                  <button className="em-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}><FiChevronLeft size={15} /></button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button key={i + 1} className={`em-page-btn${page === i + 1 ? " active" : ""}`} onClick={() => setPage(i + 1)}>{i + 1}</button>
                  ))}
                  <button className="em-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}><FiChevronRight size={15} /></button>
                </div>
              </div>
            )}
          </>
        ) : view === "detail" ? (
          <ProjectDetailsView
            project={selected}
            user={user}
            employees={employees}
            projectStatuses={projectStatuses}
            clients={clients}
            priorities={priorities}
            onBack={() => {
              setView("list");
              setSelected(null);
              fetchProjects();
            }}
            onEdit={() => {
              setInitialFormTab("details");
              setView("edit");
            }}
            onDelete={() => {
              setModal("delete");
            }}
          />
        ) : (
          <ProjectFormView
            project={selected}
            user={user}
            view={view}
            initialTab={initialFormTab}
            employees={employees}
            projectStatuses={projectStatuses}
            onBack={(savedItem) => {
              const updatedItem = savedItem || selected;
              if (view === "edit" || selected) {
                setView("detail");
                setSelected(updatedItem);
              } else {
                setView("list");
                setSelected(null);
              }
              fetchProjects();
            }}
          />
        )}
      </div>

      {/* Filter Drawer */}
      {renderFilterDrawer()}

      {modal === "delete" && selected && (
        <DeleteModal
          project={selected}
          onClose={() => closeModal(false)}
          onConfirm={() => closeModal(true)}
        />
      )}
    </MainLayout>
  );
}


/* ─── Project Form View (Side Tabs) ─── */
function ProjectFormView({ project, view, initialTab = "notifications", employees, projectStatuses = [], user, onBack }) {
  const [activeTab, setActiveTab] = useState(["details", "team"].includes(initialTab) ? "notifications" : initialTab);
  const [clients, setClients] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [designations, setDesignations] = useState([]);

  // Load master data for dropdowns
  useEffect(() => {
    api.get("/api/client-master").then(r => setClients(r.data.data || [])).catch(() => { });
    api.get("/api/priority-master").then(r => setPriorities(r.data.data || [])).catch(() => { });
    api.get("/api/designation-master").then(r => setDesignations(r.data.data || [])).catch(() => { });
  }, []);

  // Safe mapping of employees
  const safeEmployees = Array.isArray(employees) ? employees : [];
  const empOptions = safeEmployees.map(e => {
    const eid = e.employee_id || e.employeeId || e.id || e.code;
    const fname = e.first_name || e.firstName || "";
    const lname = e.last_name || e.lastName || "";
    const name = e.name || e.displayName || e.full_name || `${fname} ${lname}`.trim() || String(eid);
    const desigObj = designations.find(d => String(d.designation_id) === String(e.designation) || String(d.id) === String(e.designation) || String(d.designation_name) === String(e.designation));
    const desigName = desigObj ? desigObj.designation_name : e.designation;
    const designationStr = desigName ? ` (${desigName})` : "";
    return { id: eid, label: name + designationStr };
  });

  const dynamicStatuses = getDynamicStatuses(projectStatuses);
  const defaultStatus = dynamicStatuses.length > 0 ? dynamicStatuses[0].value : "InProgress";

  const blank = {
    projectId: generateProjectId(),
    projectName: "",
    description: "",
    startDate: "",
    endDate: "",
    dueDate: "",
    clientName: "",
    clientMasterId: "",
    priorityId: "",
    phase: "",
    notes: "",
    status: defaultStatus,
    developers: [],
    projectOwner: "",
    technicalLead: "",
    projectManager: "",
    notificationMembers: [],
  };

  const [form, setForm] = useState(project
    ? {
      projectId: project.sfprojectsid || `PRJ-${project.id}`,
      projectName: project.project_name || "",
      description: project.description || "",
      startDate: project.start_date ? project.start_date.split("T")[0] : "",
      endDate: project.end_date ? project.end_date.split("T")[0] : "",
      dueDate: project.due_date ? project.due_date.split("T")[0] : "",
      clientName: project.client_name || "",
      clientMasterId: project.client_master_id ? String(project.client_master_id) : "",
      priorityId: project.priority_id ? String(project.priority_id) : "",
      phase: project.phase || "",
      notes: project.notes || "",
      status: project.status || defaultStatus,
      developers: project.developers || [],
      projectOwner: project.project_owner || "",
      technicalLead: project.technical_lead || "",
      projectManager: project.project_manager || "",
      notificationMembers: project.notification_members || [],
    }
    : blank
  );

  const [saving, setSaving] = useState(false);
  const [savedProjectContext, setSavedProjectContext] = useState(project);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [pendingDocs, setPendingDocs] = useState([]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  useEffect(() => {
    if (view === "create" && !savedProjectContext) {
      const cleanName = (form.projectName || "").replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      const generatedId = cleanName ? `PRJ-${cleanName}-0001` : `PRJ-0001`;
      if (form.projectId !== generatedId) {
        set("projectId", generatedId);
      }
    }
  }, [form.projectName, view, savedProjectContext]);

  const loadTimeline = async (pid) => {
    setTimelineLoading(true);
    try {
      const res = await api.get(`/api/projects/${pid}/timeline`);
      setTimeline(res.data.data || []);
    } catch { setTimeline([]); }
    finally { setTimelineLoading(false); }
  };

  useEffect(() => {
    if (activeTab === "timeline" && savedProjectContext?.id) {
      loadTimeline(savedProjectContext.id);
    }
  }, [activeTab, savedProjectContext]);

  const handleSave = async (redirectAfterSave = true) => {
    let finalProjectName = form.projectName.trim();
    if (!finalProjectName) {
      toast.error("Project Name is mandatory");
      return;
    }

    if (!form.startDate) {
      toast.error("Start Date is mandatory");
      return;
    }
    if (!form.priorityId) {
      toast.error("Priority is mandatory");
      return;
    }
    if (!form.description || !form.description.trim()) {
      toast.error("Description is mandatory");
      return;
    }

    setSaving(true);
    try {
      const currentUserName = getUserDisplayName(user);
      const payload = {
        projectName: finalProjectName,
        description: form.description,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        dueDate: form.dueDate || null,
        clientName: form.clientName || null,
        clientMasterId: form.clientMasterId ? parseInt(form.clientMasterId) : null,
        priorityId: form.priorityId ? parseInt(form.priorityId) : null,
        phase: form.phase || null,
        notes: form.notes || null,
        status: form.status,
        developers: form.developers,
        projectOwner: form.projectOwner || null,
        technicalLead: form.technicalLead || null,
        projectManager: form.projectManager || null,
        notificationMembers: form.notificationMembers,
        sfprojectsid: form.projectId,
        createdBy: currentUserName,
        created_by: currentUserName,
        updatedBy: currentUserName,
        updated_by: currentUserName,
        changedBy: currentUserName,
        changed_by: currentUserName,
        userId: user?.empId || user?.employeeId || user?.id || "system",
      };

      let savedCtx = savedProjectContext;
      if (savedProjectContext) {
        const res = await api.put(`/api/projects/${savedProjectContext.id}`, payload);
        toast.success("Project updated");
        const updatedData = res?.data?.data || res?.data || res || {};
        savedCtx = {
          ...savedProjectContext,
          ...updatedData,
          project_name: payload.projectName || savedProjectContext.project_name,
          client_name: payload.clientName || savedProjectContext.client_name,
          start_date: payload.startDate || savedProjectContext.start_date,
          end_date: payload.endDate || savedProjectContext.end_date,
          due_date: payload.dueDate || savedProjectContext.due_date,
          priority_id: payload.priorityId || savedProjectContext.priority_id,
          phase: payload.phase || savedProjectContext.phase,
          client_master_id: payload.clientMasterId || savedProjectContext.client_master_id,
          status: payload.status || savedProjectContext.status,
          project_owner: payload.projectOwner || savedProjectContext.project_owner,
          technical_lead: payload.technicalLead || savedProjectContext.technical_lead,
          project_manager: payload.projectManager || savedProjectContext.project_manager,
          developers: payload.developers || savedProjectContext.developers,
          sfprojectsid: payload.sfprojectsid || savedProjectContext.sfprojectsid,
          description: payload.description || savedProjectContext.description,
          notes: payload.notes || savedProjectContext.notes
        };
        setSavedProjectContext(savedCtx);
      } else {
        const res = await api.post("/api/projects", payload);
        toast.success("Project created");
        const newProject = res?.data?.data || res?.data || res;
        setSavedProjectContext(newProject);
        savedCtx = newProject;
      }

      // Automatically link project to client if client is selected
      if (payload.clientMasterId && savedCtx?.id) {
        try {
          const clientData = clients.find(c => String(c.id) === String(payload.clientMasterId));
          if (clientData) {
            let existingIds = clientData.project_id ? (typeof clientData.project_id === 'string' ? clientData.project_id.split(',').map(Number) : clientData.project_id) : [];
            if (!Array.isArray(existingIds)) existingIds = [];
            existingIds = existingIds.filter(id => !isNaN(id) && id); // clean array
            if (!existingIds.includes(savedCtx.id)) {
              existingIds.push(savedCtx.id);
              await api.put(`/api/client-master/${payload.clientMasterId}`, { client_name: clientData.client_name, project_id: existingIds });
            }
          }
        } catch (e) {
          console.warn("Failed to auto-link project to client", e);
        }
      }

      if (pendingDocs.length > 0) {
        for (const file of pendingDocs) {
          try {
            const formData = new FormData();
            formData.append("file", file);
            await api.post(`/api/projects/${savedCtx.id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch (e) {
            toast.error(`Failed to upload ${file.name}`);
          }
        }
        setPendingDocs([]);
      }

      if (redirectAfterSave) onBack(savedCtx);
      return savedCtx;
    } catch (err) {
      const errMsg = err.response?.data?.message || err.response?.data?.error || err.message || JSON.stringify(err.response?.data) || "Failed to save project";
      toast.error(`Error: ${errMsg}`);
      console.error("Save project error:", err);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { key: "team", label: "Team Members" },
    { key: "notes", label: "Notes" },
    { key: "documents", label: "Documents" },
    { key: "notifications", label: "Notification Members" },

    // { key: "timeline", label: "Timeline" },
  ];

  /* ── Status config lookup ── */
  const priorityName = (id) => priorities.find(p => String(p.id) === String(id))?.priority_name || "—";
  const clientDisplayName = (id) => clients.find(c => String(c.id) === String(id))?.client_name || "—";

  return (
    <div style={{ display: 'flex', background: '#fff', borderRadius: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.06)', overflow: 'hidden' }}>

      {/* LEFT: MAIN FORM */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px', borderBottom: '1px solid #f1f5f9' }}>
          <div>
            <h2 className="pm-title" style={{ fontSize: 20, margin: 0 }}>{view === "create" && !savedProjectContext ? "New Project" : "Edit Project"}</h2>
            <p className="pm-sub" style={{ margin: '2px 0 0' }}>{savedProjectContext ? form.projectName : "Fill in the details below"}</p>
          </div>
        </div>

        <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', background: '#f8fafc' }}>
          {/* === ALWAYS VISIBLE TOP SECTION === */}
          <div style={{ padding: '32px 28px' }}>

            {/* STATUS STEPPER */}
            <div style={{ marginBottom: 30, display: 'flex', alignItems: 'center', background: '#f8fafc', padding: '16px 24px', borderRadius: '12px', overflowX: 'auto', border: '1px solid #e2e8f0', width: '100%' }}>
              {getDynamicStatuses(projectStatuses).map((s, idx, arr) => {
                const isActive = form.status === s.value || form.status === s.label;
                const statusIdx = arr.findIndex(x => x.value === form.status || x.label === form.status);
                const isPast = idx <= (statusIdx === -1 ? 0 : statusIdx);
                return (
                  <React.Fragment key={s.value}>
                    <div
                      onClick={() => set("status", s.value)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                        padding: '8px 20px',
                        background: isActive ? s.color : (isPast ? s.bg : '#fff'),
                        color: isActive ? '#fff' : (isPast ? s.color : '#64748b'),
                        borderRadius: '24px', fontWeight: 600, fontSize: 13,
                        border: isActive || isPast ? `1px solid ${s.color}` : '1px solid #cbd5e1',
                        transition: 'all 0.2s',
                        boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {isPast && !isActive ? <FiCheckSquare size={14} /> : null}
                      {s.label}
                    </div>
                    {idx < arr.length - 1 && (
                      <div style={{ flex: 1, minWidth: '40px', maxWidth: '80px', height: '2px', background: isPast ? s.color : '#cbd5e1', margin: '0 8px', transition: 'all 0.2s' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* ── BASIC DETAILS ── */}
            <div className="pm-section-card" style={{ background: '#fff', padding: '28px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', marginBottom: 32 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* 1. Project ID */}
                <div className="pm-field">
                  <label className="pm-label"><FiTag size={12} /> Project ID</label>
                  <input className="pm-input pm-input-readonly" value={form.projectId} readOnly />
                </div>

                {/* 2. Project Name */}
                <div className="pm-field">
                  <label className="pm-label">Project Name <span style={{ color: '#ef4444' }}>*</span></label>
                  <input className="pm-input" placeholder="Enter Project Name" value={form.projectName} onChange={e => set("projectName", e.target.value)} />
                </div>

                {/* 3. Client Name */}
                <div className="pm-field">
                  <label className="pm-label"><FiUser size={12} /> Client Name</label>
                  <select
                    className="pm-input pm-select"
                    value={form.clientMasterId}
                    onChange={e => {
                      const cid = e.target.value;
                      const cname = clients.find(c => String(c.id) === cid)?.client_name || "";
                      setForm(f => ({ ...f, clientMasterId: cid, clientName: cname }));
                    }}
                  >
                    <option value="">— Select Client —</option>
                    {clients.map(c => <option key={c.id} value={String(c.id)}>{c.client_name}</option>)}
                  </select>
                </div>

                {/* 4. Priority */}
                <div className="pm-field">
                  <label className="pm-label">Priority <span style={{ color: '#ef4444' }}>*</span></label>
                  <select
                    className="pm-input pm-select"
                    value={form.priorityId}
                    onChange={e => set("priorityId", e.target.value)}
                  >
                    <option value="">— Select Priority —</option>
                    {priorities.map(p => <option key={p.id} value={String(p.id)}>{p.priority_name}</option>)}
                  </select>
                </div>

                {/* Phase */}
                <div className="pm-field">
                  <label className="pm-label">Phase</label>
                  <input className="pm-input" placeholder="Enter Phase" value={form.phase} onChange={e => set("phase", e.target.value)} />
                </div>

                {/* 5. Start Date */}
                <div className="pm-field">
                  <label className="pm-label"><FiCalendar size={12} /> Start Date <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="date" className="pm-input" value={form.startDate} onChange={e => set("startDate", e.target.value)} />
                </div>

                {/* 6. End Date (Optional) */}
                <div className="pm-field">
                  <label className="pm-label"><FiCalendar size={12} /> End Date</label>
                  <input type="date" className="pm-input" value={form.endDate} min={form.startDate} onChange={e => set("endDate", e.target.value)} />
                </div>

                {/* 7. Due Date (Optional) */}
                <div className="pm-field">
                  <label className="pm-label"><FiCalendar size={12} /> Due Date</label>
                  <input type="date" className="pm-input" value={form.dueDate} min={form.startDate} onChange={e => set("dueDate", e.target.value)} />
                </div>

                {/* Empty field for grid alignment if needed */}
                <div className="pm-field"></div>

                {/* 8. Description (Span both columns) */}
                <div className="pm-field" style={{ gridColumn: '1 / -1' }}>
                  <label className="pm-label">Description <span style={{ color: '#ef4444' }}>*</span></label>
                  <textarea className="pm-textarea" placeholder="Describe the project..." rows={4} value={form.description} onChange={e => set("description", e.target.value)} />
                </div>
              </div>
            </div>

          </div>

          {/* === TABS BAR === */}
          <div style={{ padding: '0 28px', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
            <div style={{ display: 'flex', gap: '32px' }}>
              {TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  style={{
                    padding: '16px 0',
                    background: 'none', border: 'none',
                    borderBottom: activeTab === t.key ? '3px solid #6366f1' : '3px solid transparent',
                    color: activeTab === t.key ? '#6366f1' : '#64748b',
                    fontWeight: activeTab === t.key ? 700 : 500,
                    fontSize: 14, cursor: 'pointer', outline: 'none',
                    transition: 'all 0.2s',
                    marginBottom: '-1px'
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* === TAB CONTENT === */}
          <div style={{ padding: '32px 28px 120px 28px', minHeight: 300 }}>

            {/* ── TEAM & ACCESS ── */}
            {activeTab === "team" && (
              <div className="pm-section-card" style={{ background: '#fff', padding: '28px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 20px 0', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiUsers size={16} style={{ color: '#10b981' }} /> Team members
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div className="pm-form-row">
                    <div className="pm-field">
                      <label className="pm-label"><FiUser size={12} /> Project Owner</label>
                      <SingleSelect options={empOptions} value={form.projectOwner} onChange={v => set("projectOwner", v)} placeholder="Select owner" />
                    </div>
                    <div className="pm-field">
                      <label className="pm-label"><FiUser size={12} /> Technical Lead</label>
                      <SingleSelect options={empOptions} value={form.technicalLead} onChange={v => set("technicalLead", v)} placeholder="Select tech lead" />
                    </div>
                  </div>
                  <div className="pm-form-row">
                    <div className="pm-field">
                      <label className="pm-label"><FiUser size={12} /> Project Manager</label>
                      <SingleSelect options={empOptions} value={form.projectManager} onChange={v => set("projectManager", v)} placeholder="Select project manager" />
                    </div>
                    <div className="pm-field">
                      <label className="pm-label"><FiUsers size={12} /> Developers</label>
                      <MultiSelect options={empOptions} selected={form.developers} onChange={v => set("developers", v)} placeholder="Select developers..." />
                    </div>
                  </div>
                </div>
              </div>
            )}


            {/* ── NOTIFICATION MEMBERS ── */}
            {activeTab === "notifications" && (
              <div className="pm-section-card" style={{ background: '#fff', padding: '28px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 20px 0', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiBell size={16} style={{ color: '#f59e0b' }} /> Notification Members
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '14px 16px', border: '1px solid #e0e7ff' }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#4338ca' }}>
                      <FiBell size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                      Members selected here will receive project notifications and alerts.
                    </p>
                  </div>
                  <div className="pm-field">
                    <label className="pm-label"><FiBell size={12} /> Notification Members</label>
                    <MultiSelect options={empOptions} selected={form.notificationMembers} onChange={v => set("notificationMembers", v)} placeholder="Select notification members..." />
                  </div>
                </div>
              </div>
            )}

            {/* ── NOTES ── */}
            {activeTab === "notes" && (
              <div className="pm-section-card" style={{ background: '#fff', padding: '28px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 20px 0', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiUser size={16} style={{ color: '#ec4899' }} /> Notes
                </h3>
                <div className="pm-field" style={{ height: 400 }}>
                  <label className="pm-label">Notes</label>
                  <div className="pm-quill-wrap" style={{ height: 'calc(100% - 30px)' }}>
                    <ReactQuill
                      value={form.notes}
                      onChange={val => set("notes", val)}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Add notes here..."
                      theme="snow"
                      style={{ height: '85%' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* ── DOCUMENTS ── */}
            {activeTab === "documents" && (
              <div className="pm-section-card" style={{ background: '#fff', padding: '28px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 20px 0', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiPaperclip size={16} style={{ color: '#3b82f6' }} /> Documents
                </h3>
                <ProjectDocumentsTab
                  project={savedProjectContext}
                  isPending={!savedProjectContext}
                  pendingDocs={pendingDocs}
                  setPendingDocs={setPendingDocs}
                />
              </div>
            )}

          </div>
        </div>

        {/* Footer / Save Bottom Buttons */}
        <div style={{ padding: '20px 28px', borderTop: '1px solid #f1f5f9', background: '#fff', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button className="pm-btn pm-btn-cancel" onClick={onBack}>Cancel</button>
          <button className="pm-btn pm-btn-primary" onClick={() => handleSave(true)} disabled={saving}>
            {saving ? "Saving…" : (savedProjectContext || view === 'edit') ? "Update Project" : "Create Project"}
          </button>
        </div>

      </div> {/* End LEFT: MAIN FORM */}

      {/* RIGHT: STATUS TIMELINE SIDEBAR */}
      {savedProjectContext && (
        <div style={{ width: 340, flexShrink: 0, background: '#fafbff', borderLeft: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 120px)' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiLayers size={16} style={{ color: '#4F6EF7' }} /> Status Timeline
            </h3>
          </div>
          <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
            {timelineLoading ? (
              <div className="pm-loading"><div className="pm-spinner" /><span>Loading timeline...</span></div>
            ) : timeline.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No timeline events yet.</div>
            ) : (
              <div style={{ position: 'relative', paddingLeft: 24 }}>
                {/* Vertical line */}
                <div style={{ position: 'absolute', left: 7, top: 0, bottom: 0, width: 2, background: '#e2e8f0' }} />
                {timeline.map((t, i) => {
                  const cfg = t.action === 'Created'
                    ? { color: '#10b981', bg: '#ecfdf5' }
                    : t.action === 'Status Changed'
                      ? { color: '#f59e0b', bg: '#fffbeb' }
                      : { color: '#4F6EF7', bg: '#eff6ff' };
                  return (
                    <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24, position: 'relative' }}>
                      {/* Dot */}
                      <div style={{ width: 16, height: 16, borderRadius: '50%', background: cfg.bg, border: `2px solid ${cfg.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color }} />
                      </div>
                      {/* Content */}
                      <div style={{ flex: 1, background: '#fff', borderRadius: 10, padding: '12px 14px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>{t.action}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 6px' }}>{fmtDateTime(t.changed_at)}</div>

                        {t.action === 'Status Changed' && (
                          <div style={{ fontSize: 12, color: '#475569' }}>
                            <span style={{ background: '#fef2f2', color: '#ef4444', borderRadius: 4, padding: '2px 5px', fontWeight: 600 }}>{t.old_value}</span>
                            <span style={{ margin: '0 4px', color: '#94a3b8' }}>→</span>
                            <span style={{ background: '#f0fdf4', color: '#10b981', borderRadius: 4, padding: '2px 5px', fontWeight: 600 }}>{t.new_value}</span>
                          </div>
                        )}
                        {t.action !== 'Status Changed' && t.new_value && (
                          <div style={{ fontSize: 12, color: '#475569' }}>
                            Status: <span style={{ fontWeight: 600, color: '#4F6EF7' }}>{t.new_value}</span>
                          </div>
                        )}
                        {t.changed_by && (
                          <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>
                            By: {(() => {
                              if (!t.changed_by || t.changed_by === 'system') return 'System';
                              return empName(t.changed_by);
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

/* ─── Project Documents Tab Component ─── */
function ProjectDocumentsTab({ project, readOnly = false, isPending = false, pendingDocs = [], setPendingDocs = () => { } }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(!isPending);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    if (isPending || !project?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get(`/api/projects/${project.id}/documents`);
      setDocs(res.data?.data || res.data || []);
    } catch (err) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [project?.id, isPending]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (readOnly) return;
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const uploadFile = async (file) => {
    const allowedTypes = [
      "application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "image/jpeg", "image/jpg", "image/png"
    ];

    const ext = file.name.split(".").pop().toLowerCase();
    const isAllowedExt = ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png"].includes(ext);

    if (!allowedTypes.includes(file.type) && !isAllowedExt) {
      toast.error(`Invalid file type: ${file.name}.`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`File too large: ${file.name}. Max 10MB.`);
      return;
    }

    if (isPending) {
      setPendingDocs((prev) => [...prev, file]);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      await api.post(`/api/projects/${project.id}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded: ${file.name}`);
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.error || `Failed to upload ${file.name}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (readOnly) return;
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files);
      for (const file of files) await uploadFile(file);
    }
  };

  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const files = Array.from(e.target.files);
      for (const file of files) await uploadFile(file);
    }
  };

  const onButtonClick = () => {
    if (!readOnly) fileInputRef.current.click();
  };

  const handleDelete = async (docId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) return;
    try {
      await api.delete(`/api/projects/${project.id}/documents/${docId}`);
      toast.success("Document deleted");
      fetchDocs();
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const handleDeletePending = (index) => {
    setPendingDocs((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileClassAndIcon = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return { class: "image", label: "IMG" };
    if (["xls", "xlsx", "csv"].includes(ext)) return { class: "excel", label: "XLS" };
    if (["doc", "docx"].includes(ext)) return { class: "word", label: "DOC" };
    if (ext === "pdf") return { class: "pdf", label: "PDF" };
    return { class: "other", label: "FILE" };
  };

  const allRenderedDocs = isPending
    ? pendingDocs.map((f, i) => ({ isPending: true, file: f, index: i, file_name: f.name, size: Math.round(f.size / 1024) + " KB" }))
    : docs;

  return (
    <div>
      {!readOnly && (
        <div
          className={`pm-dropzone ${dragActive ? "drag-active" : ""}`}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          onClick={onButtonClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            style={{ display: "none" }}
          />
          <FiUploadCloud className="pm-dropzone-icon" />
          <p className="pm-dropzone-text">
            Drag &amp; drop file here, or <span>Browse files</span>
          </p>
          <p className="pm-dropzone-sub">
            Supports: PDF, Excel, JPEG, PNG, DOC (Max 5MB)
          </p>
        </div>
      )}

      {uploading && (
        <div className="pm-doc-uploading">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FiLoader className="pm-spinner" style={{ animationDuration: "1s" }} />
            <span>Uploading document...</span>
          </div>
          <div className="pm-doc-progress-bar">
            <div className="pm-doc-progress-fill" style={{ width: "60%" }}></div>
          </div>
        </div>
      )}

      <h3 className="pm-label" style={{ marginBottom: 4, marginTop: readOnly ? 0 : 16 }}>
        {isPending ? "Selected Files" : "Uploaded Files"} ({allRenderedDocs.length})
      </h3>

      {loading ? (
        <div className="pm-loading" style={{ padding: "30px" }}>
          <div className="pm-spinner" />
          <span>Loading documents...</span>
        </div>
      ) : allRenderedDocs.length === 0 ? (
        <div className="pm-empty" style={{ padding: "30px 10px", background: "#f8fafc", borderRadius: 8 }}>
          <div className="pm-empty-icon" style={{ fontSize: 24 }}>📂</div>
          <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
            {isPending ? "No files selected yet." : "No documents uploaded yet."}
          </p>
        </div>
      ) : (
        <div className="pm-doc-list">
          {allRenderedDocs.map((doc, idx) => {
            const info = getFileClassAndIcon(doc.file_name);
            return (
              <div key={doc.id || idx} className="pm-doc-card">
                <div className="pm-doc-info">
                  <div className={`pm-doc-icon-wrap ${info.class}`}>{info.label}</div>
                  <div className="pm-doc-meta">
                    <span className="pm-doc-name" title={doc.file_name}>{doc.file_name}</span>
                    <div className="pm-doc-size-date">
                      {doc.isPending ? (
                        <span style={{ color: '#f59e0b', fontWeight: 600 }}>Unsaved • {doc.size}</span>
                      ) : (
                        <>
                          <span>{doc.file_size}</span>
                          <span className="pm-doc-bullet">•</span>
                          <span>{fmtDateTime(doc.uploaded_at)}</span>
                          {doc.uploaded_by && (
                            <>
                              <span className="pm-doc-bullet">•</span>
                              <span>By: {doc.uploaded_by}</span>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="pm-doc-actions">
                  {doc.signed_url && (
                    <a href={doc.signed_url} target="_blank" rel="noopener noreferrer" className="pm-doc-btn download" title="Download">
                      <FiDownload size={14} />
                    </a>
                  )}
                  {!readOnly && (
                    <button
                      className="pm-doc-btn delete"
                      onClick={() => doc.isPending ? handleDeletePending(doc.index) : handleDelete(doc.id, doc.file_name)}
                      title="Delete"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
